import {
  Button,
  Checkbox,
  CheckboxGroup,
  CloseButton,
  Flex,
  Heading,
  IconDownloadLine,
  Link,
  List,
  Modal,
  Spinner,
  Text,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useContext, useState } from "react";
import { Trans } from "react-i18next";
import "react-loading-skeleton/dist/skeleton.css";
import { CourseService } from "../../../../services/CourseService";
import { Files } from "../../../../services/FilesService";
import { TaskService } from "../../../../services/TaskService";
import { UserDetailContext } from "../../../../utils/Context";
import { downloadCSVTemplate } from "../../../../utils/DownloadCSVTemplate";
import {
  csvFileEnricher,
  fileReader,
  fileWriter,
} from "../../../../utils/FileUtils";
import { awaitTask, waitForExpectedResult } from "../../../../utils/Timer";
import { Card } from "../../../Card";
import CurriculumMapTermSelect from "../../../CurriculumMap/CurriculumMapTermSelect";
import {
  ERROR,
  SUCCESS,
  useNotification,
} from "../../../CustomHooks/NotificationProvider";
import {
  FAIL,
  GOOD,
  WORKING,
  useStatus,
} from "../../../CustomHooks/StatusProvider";
import { DialogYesNo } from "../../../DialogYesNo/DialogYesNo";
import EnrichedFileDrop from "../../../LearningOutcomes/FileDrop/EnrichedFileDrop";
import {
  getCurrentVersion,
  isArchived,
  pendingArchive,
  sortOutcomes,
} from "../../../LearningOutcomes/utils";
import { getEarliestCanvasTerm } from "../../../TermManagement/utils";
import { enrichBulkCLOs } from "../utils";

const LEARNING_OUTCOMES_FILE_TYPE = "learningOutcomes";
const PAGE_SIZE = 1000;
const OUTCOME_PAGE_SIZE = 300;

const BulkCLOUploadModal = ({ uploadDialog, setUploadDialog }) => {
  const showNotification = useNotification();
  const { updateStatus, toggleStatus } = useStatus();
  const [LOFile, setLOFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [effectiveTerm, setEffectiveTerm] = useState({});
  const [openConfirm, setOpenConfirm] = useState(false);
  const userDetails = useContext(UserDetailContext);

  const handleSubmit = async () => {
    try {
      // Enrich the file on submit
      const termId = getEarliestCanvasTerm(effectiveTerm)?.id;
      const file = LOFile;
      const fr = await fileReader(file);
      const content = fr.result;
      const enrichedContent = csvFileEnricher(
        content,
        enrichBulkCLOs(termId, templateOptions.includes("autoApprove"))
      );
      const enrichedFile = fileWriter(
        enrichedContent,
        LOFile.name,
        LOFile.type
      );

      const { taskId } = await Files.saveFile(
        enrichedFile,
        LEARNING_OUTCOMES_FILE_TYPE,
        null,
        true
      );
      setUploadDialog(false);
      setLOFile(null);
      fetchTask(taskId);
    } catch (error) {
      console.debug(error);
      showNotification(t("learningOutcomes.uploadError"), ERROR);
    }
  };

  const fetchTask = async (taskId) => {
    showNotification(
      <Text>
        {t("notifications.clo.cloUploadInProgress")}
        <Link
          themeOverride={{ fontWeight: "bold" }}
          onClick={() => toggleStatus(true)}
        >
          {t("status.checkStatus")}
        </Link>
      </Text>,
      SUCCESS,
      10000
    );
    updateStatus({
      service: {
        name: t("status.cloUploadStatus"),
        status: WORKING,
        progress: 0,
      },
    });

    await awaitTask(taskId);

    waitForExpectedResult(
      () => TaskService.getTaskById(taskId),
      (task) => {
        const progress = parseInt(
          (task.counter / task.obj.total_outcomes) * 100
        );
        updateStatus({
          service: {
            name: t("status.cloUploadStatus"),
            status: progress === 100 ? GOOD : WORKING,
            progress: progress,
          },
        });
        return task.status === "done" || task.status === "error";
      },
      processPushFileTask,
      () => {
        showNotification(t("notifications.clo.cloUploadFailed"), ERROR);
        updateStatus({
          service: { name: t("status.cloUploadStatus"), status: FAIL },
        });
      },
      500,
      5000
    );
  };

  function processPushFileTask(task) {
    if (task.status === "done") {
      showNotification(t("notifications.clo.cloUploadComplete"), SUCCESS);
    } else if (task.status === "error") {
      showNotification(t("notifications.clo.cloUploadFailed"), ERROR);
      updateStatus({
        service: { name: t("status.cloUploadStatus"), status: FAIL },
      });
    }
  }

  const handleDownload = async () => {
    const conditionalPageSize = templateOptions.includes("versionArchive")
      ? OUTCOME_PAGE_SIZE
      : PAGE_SIZE;
    setDownloading(true);
    try {
      const allCourses = [];
      let pageCount = 0;
      let refetch = true;
      while (refetch) {
        const courses = await CourseService.getCourses({
          ownerCourseCode:
            userDetails.role == "pc" ? userDetails.ac_subjects : "",
          approval: "off",
          page: pageCount,
          pageSize: conditionalPageSize,
          withOutcomes: templateOptions.includes("versionArchive"),
          currentOutcomesOnly: templateOptions.includes("versionArchive"),
          includeFutureOutcomes: templateOptions.includes("versionArchive"),
        });
        allCourses.push(...courses.data);
        if (courses.data.length < conditionalPageSize) {
          refetch = false;
        } else {
          pageCount++;
        }
      }

      let headers = [];
      if (templateOptions.includes("versionArchive")) {
        headers = [
          "context_id",
          "offering_id",
          "academic_subject",
          "offering_code",
          "offering_name",
          "current_version_outcome_id",
          "title",
          "description",
          "future_version_exists",
          "new_title",
          "new_description",
          "archive",
          "effective_term_name",
        ];
      } else {
        headers = [
          "context_id",
          "offering_id",
          "academic_subject",
          "offering_code",
          "offering_name",
          "title",
          "description",
        ];
      }

      const coursesData = [];

      allCourses.forEach((course) => {
        course.offerings.forEach((offering) => {
          if (
            course.academic_subject &&
            (offering.is_default || offering.has_unique_los)
          ) {
            if (templateOptions.includes("versionArchive")) {
              sortOutcomes(offering.learning_outcomes).forEach((clo) => {
                if (
                  !clo.unapproved &&
                  !isArchived(clo) &&
                  !pendingArchive(clo)
                ) {
                  const currentClo = getCurrentVersion(clo);
                  const toPush = {
                    context_id: course.id,
                    offering_id: offering.id,
                    academic_subject: course.academic_subject.title,
                    offering_code: offering.code,
                    offering_name:
                      offering.course_summary.representative_section_name ||
                      "No representative offering name found",
                    current_version_outcome_id: currentClo.id,
                    title: currentClo.name,
                    description: currentClo.statement,
                    future_version_exists: clo.version_history.length > 0,
                    new_title: "",
                    new_description: "",
                    archive: "",
                    effective_term_name: effectiveTerm.name,
                  };
                  coursesData.push(toPush);
                }
              });
            } else {
              const toPush = {
                context_id: course.id,
                offering_id: offering.id,
                academic_subject: course.academic_subject.title,
                offering_code: offering.code,
                offering_name:
                  offering.course_summary.representative_section_name ||
                  "No representative offering name found",
                title: "",
                description: "",
              };
              coursesData.push(toPush);
            }
          }
        });
      });

      downloadCSVTemplate("CLO_Template.csv", headers, coursesData);
    } catch (e) {
      console.debug(e);
      showNotification(t("notifications.general.downloadError"), ERROR);
    } finally {
      setDownloading(false);
    }
  };

  const handleTemplateOptionsChange = (value) => {
    if (value.includes("versionArchive")) {
      setTemplateOptions(["versionArchive", "autoApprove"]);
    } else {
      setTemplateOptions(value);
      setEffectiveTerm({});
    }
  };

  const confirmationText = () => {
    let option = "";
    if (
      !templateOptions.includes("versionArchive") &&
      !templateOptions.includes("autoApprove")
    ) {
      option = "in an unapproved state";
    } else if (
      !templateOptions.includes("versionArchive") &&
      templateOptions.includes("autoApprove")
    ) {
      option = "auto-approved, as new";
    } else if (
      templateOptions.includes("versionArchive") &&
      templateOptions.includes("autoApprove")
    ) {
      option = "auto-approved, as versions/archived for " + effectiveTerm.name;
    }
    return (
      t("learningOutcomes.confirmMessage1") +
      option +
      ". " +
      t("learningOutcomes.confirmMessage2")
    );
  };

  return (
    <>
      <Modal
        open={uploadDialog}
        label={t("global.dragFile")}
        onExited={() => {
          setLOFile(null);
          setTemplateOptions([]);
          setEffectiveTerm({});
        }}
      >
        <Modal.Header>
          <Heading level="h2" as="h1">
            {t("learningOutcomes.cloUpload")}
          </Heading>
          <CloseButton
            placement="end"
            offset="medium"
            screenReaderLabel={t("global.close")}
            onClick={() => setUploadDialog(false)}
          />
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: 10 }}>
            <EnrichedFileDrop
              fileState={LOFile}
              setFileState={setLOFile}
              fileDropHeading={t("clo.dropFileHeading")}
              fileExtension=".csv"
            />
          </div>
          <Card style={{ margin: "20px 0" }}>
            <Flex wrap="wrap" gap="small">
              <Flex.Item size="100%">
                <CheckboxGroup
                  name="templateOptions"
                  description={t("learningOutcomes.templateOptions")}
                  size="small"
                  value={templateOptions}
                  onChange={handleTemplateOptionsChange}
                >
                  <Checkbox
                    label={t("learningOutcomes.autoApprove")}
                    value="autoApprove"
                    disabled={templateOptions.includes("versionArchive")}
                  />
                  <Checkbox
                    label={t("learningOutcomes.versionArchive")}
                    value="versionArchive"
                  />
                </CheckboxGroup>
              </Flex.Item>
            </Flex>
          </Card>
          <Text as="div">{t("clo.uploadLine1")}</Text>
          {templateOptions.includes("versionArchive") ? (
            <List margin="small 0">
              <List.Item>{t("versionArchive.item1")}</List.Item>
              <List.Item>{t("versionArchive.item2")}</List.Item>
              <List.Item>{t("versionArchive.item3")}</List.Item>
              <List.Item>{t("versionArchive.item4")}</List.Item>
            </List>
          ) : (
            <List margin="small 0">
              <List.Item>{t("clo.item1")}</List.Item>
              <List.Item>{t("clo.item2")}</List.Item>
              <List.Item>{t("clo.item3")}</List.Item>
            </List>
          )}
          <Text as="div" weight="bold">
            {templateOptions.includes("versionArchive")
              ? t("versionArchive.footer1")
              : t("clo.item4")}
          </Text>
          {templateOptions.includes("versionArchive") && (
            <>
              <br />
              <Text as="div" fontStyle="italic">
                {t("versionArchive.footer2")}
              </Text>
            </>
          )}
          <br />
          <Trans i18nKey="versionArchive.dataDictionary">
            A data dictionary for the selected template options is available
            <a
              href={
                templateOptions.includes("versionArchive")
                  ? "/cloVersionArchiveDataDictionary.txt"
                  : "/cloUploadDataDictionary.txt"
              }
              download
            >
              here
            </a>
            .
          </Trans>
          <View as="div" borderWidth="small 0 0 0" margin="small 0" />
          <Flex wrap="wrap" gap="small" alignItems="end">
            {templateOptions.includes("versionArchive") && (
              <Flex.Item>
                <CurriculumMapTermSelect
                  id="effective-term-select"
                  label={t("learningOutcomes.effectiveTerm")}
                  selectHandler={(term) => setEffectiveTerm(term)}
                  selectedTermId={effectiveTerm.id}
                />
              </Flex.Item>
            )}
            <Flex.Item>
              <Button
                color="primary"
                renderIcon={
                  downloading ? (
                    <Spinner
                      renderTitle={t("global.pleaseWait")}
                      size="x-small"
                    />
                  ) : (
                    IconDownloadLine
                  )
                }
                onClick={handleDownload}
                disabled={
                  downloading ||
                  (templateOptions.includes("versionArchive") &&
                    !effectiveTerm.id)
                }
              >
                {templateOptions.includes("versionArchive")
                  ? t("clo.downloadTemplate") + " (Version / Archive)"
                  : t("clo.downloadTemplate")}
              </Button>
            </Flex.Item>
          </Flex>
        </Modal.Body>
        <Modal.Footer>
          <Button margin="0 small 0 0" onClick={() => setUploadDialog(false)}>
            {t("global.cancel")}
          </Button>
          <Button
            color="primary"
            onClick={() => setOpenConfirm(true)}
            disabled={
              !LOFile ||
              (templateOptions.includes("versionArchive") && !effectiveTerm.id)
            }
          >
            {t("global.submit")}
          </Button>
        </Modal.Footer>
      </Modal>
      <DialogYesNo
        message={confirmationText()}
        openStatus={openConfirm}
        onClickNo={() => setOpenConfirm(false)}
        onClickYes={async () => {
          await handleSubmit();
          setOpenConfirm(false);
        }}
      />
    </>
  );
};

export default BulkCLOUploadModal;
